/**
 * Utilidades para manejo de archivos y conversiones
 */

/**
 * Convierte una cadena base64 a un objeto Blob
 *
 * @param base64 - Cadena base64 o data URL
 * @returns Promise que resuelve a un Blob
 * @throws Error si la conversión falla
 *
 * @example
 * ```typescript
 * const blob = await base64ToBlob('data:image/jpeg;base64,...');
 * ```
 */
export async function base64ToBlob(base64: string): Promise<Blob> {
  if (!base64) {
    throw new Error('La cadena base64 es requerida');
  }

  try {
    const response = await fetch(base64);
    return response.blob();
  } catch (error) {
    throw new Error(`Error al convertir base64 a Blob: ${error instanceof Error ? error.message : 'Error desconocido'}`);
  }
}

/**
 * Convierte una data URL a File conservando el mime type
 *
 * @param dataUrl - Data URL con el contenido base64 (data:image/jpeg;base64,...)
 * @param fileName - Nombre final del archivo (incluye extensión)
 */
export async function dataUrlToFile(dataUrl: string, fileName: string): Promise<File> {
  if (!dataUrl?.trim()) {
    throw new Error('La data URL es requerida');
  }
  if (!fileName?.trim()) {
    throw new Error('El nombre de archivo es requerido');
  }

  const mimeMatch = dataUrl.match(/^data:(.*?);base64,/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const blob = await base64ToBlob(dataUrl);
  return new File([blob], fileName, { type: mimeType });
}

/**
 * Obtiene el tamaño formateado de un archivo
 *
 * @param bytes - Tamaño en bytes
 * @returns Cadena formateada (ej: "1.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Valida el tipo MIME de un archivo
 *
 * @param file - Archivo a validar
 * @param allowedTypes - Tipos MIME permitidos
 * @returns true si el tipo es válido
 */
export function isValidFileType(file: File | Blob, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type);
}

/**
 * Valida el tamaño de un archivo
 *
 * @param file - Archivo a validar
 * @param maxSizeInBytes - Tamaño máximo permitido en bytes
 * @returns true si el tamaño es válido
 */
export function isValidFileSize(file: File | Blob, maxSizeInBytes: number): boolean {
  return file.size <= maxSizeInBytes;
}

/**
 * Genera un nombre de archivo seguro para la imagen asociada al remito.
 *
 * @param qrCode - Código QR utilizado como base del nombre
 * @param timestamp - Timestamp que asegura unicidad (ms desde epoch)
 */
export function buildImageFileName(qrCode: string, timestamp: number = Date.now()): string {
  const fallback = 'remito';
  const base = qrCode?.trim() ? qrCode.trim() : fallback;
  const sanitized = base
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || fallback;

  return `${sanitized}_${timestamp}.jpg`;
}
